import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from '@/shared/ui/icons';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Section } from '@/shared/ui/layout/Section';
import { Input } from '@/shared/ui/inputs/Input';
import { Button } from '@/shared/ui/buttons/Button';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiParcelsClaimPost } from '@/entities/parcels';
import { claimSchema, type ClaimValues } from '@/features/claim';
import { env } from '@/shared/lib/env';

// Trek ID upper-bound matches the owner spec (5–255 chars). We auto-uppercase
// to keep zod's [A-Z0-9-] regex happy regardless of how the user types it.
const TREK_ID_MAX = 255;

export const WidgetsParcelClaimMain = () => {
  const { t } = useTranslation('claim');
  const navigate = useNavigate();
  const toast = useToast();
  const form = useForm<ClaimValues>({
    resolver: zodResolver(claimSchema),
    defaultValues: { pinfl: '', trek_id: '' },
    mode: 'onBlur',
  });
  const trekIdField = form.register('trek_id');

  const claim = useMutate({
    api: (values: ClaimValues) => apiParcelsClaimPost(values),
    invalidateKeys: [queryKeys.parcels.all],
    onSuccess: (parcel) => {
      toast.success(t('toast.added'));
      navigate(`/parcels/${parcel.id}`);
    },
    onError: (_e, message) => toast.error(message),
  });

  return (
    <>
      <PageHeader title={t('title')} subtitle={t('subtitle')} showBack />
      <form
        onSubmit={form.handleSubmit((values) => claim.mutate(values))}
        className="flex flex-col gap-4 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6"
        data-cy="claim-form"
      >
        <Section title={t('sectionTitle')} description={t('sectionDescription')}>
          <Input
            label={t('pinfl')}
            required
            inputMode="numeric"
            maxLength={env.pinflLength}
            placeholder={t('pinflPlaceholder', { length: env.pinflLength })}
            error={
              form.formState.errors.pinfl?.message
                ? t(form.formState.errors.pinfl.message, { length: env.pinflLength })
                : undefined
            }
            {...form.register('pinfl')}
          />
          <Input
            label={t('trekId')}
            required
            placeholder={t('trekIdPlaceholder')}
            autoCapitalize="characters"
            maxLength={TREK_ID_MAX}
            error={
              form.formState.errors.trek_id?.message
                ? t(form.formState.errors.trek_id.message)
                : undefined
            }
            {...trekIdField}
            onChange={(e) => {
              const v = e.target.value.toUpperCase();
              e.target.value = v;
              void trekIdField.onChange(e);
            }}
          />
        </Section>
        <Button
          type="submit"
          loading={claim.isPending}
          leftIcon={<SearchIcon className="h-4 w-4" />}
          block
          size="lg"
        >
          {t('submit')}
        </Button>
      </form>
    </>
  );
};
